import { useEffect, useState } from "react";
import { Link, useBlocker, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ChevronDown, Gavel, LockKeyhole } from "lucide-react";
import styles from "@/components/workspace/CommitVisual.module.css";
import { AiBlock } from "@/components/ax/AiBlock";
import { Button } from "@/components/ax/Button";
import { CommitSeal } from "@/components/ax/CommitSeal";
import { Field } from "@/components/ax/Field";
import { PrimaryBar } from "@/components/ax/PrimaryBar";
import { RadioList } from "@/components/ax/RadioList";
import { Sheet } from "@/components/ax/Sheet";
import { useI18n } from "@/i18n";
import { commitDecision, getDecision } from "@/lib/decisionStore";

const DEFER = "__defer__";
const isoDay = (d) => d.toISOString().slice(0, 10);
const inDays = (n) => isoDay(new Date(Date.now() + n * 86400000));

export default function Commit() {
  const { t } = useI18n();
  const { id } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState("loading");
  const [d, setD] = useState(null);
  const [choice, setChoice] = useState("");
  const [rationale, setRationale] = useState("");
  const [touched, setTouched] = useState(false);
  const [nextAction, setNextAction] = useState("");
  const [review, setReview] = useState(() => inDays(30));
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    getDecision(id).then((r) => {
      if (r.error) return setState("missing");
      if (r.decision.status === "committed" || r.decision.status === "deferred" || r.decision.options.length < 2) {
        return navigate(`/keputusan/${id}`, { replace: true });
      }
      setD(r.decision);
      setState("ready");
    });
  }, [id, navigate]);

  const dirty = !done && !!(choice || rationale || nextAction);
  const blocker = useBlocker(({ currentLocation, nextLocation }) => dirty && currentLocation.pathname !== nextLocation.pathname);

  const short = rationale.trim().length < 20;
  const valid = !short && !!choice && !!review;
  const rec = d?.brief && !d.brief.abstained ? d.brief.recommendation : null;
  const recOption = rec && d.options.find((o) => o.id === rec.optionId);
  const differs = !!choice && !!recOption && choice !== recOption.id;

  const submit = async () => {
    if (!valid || busy) return;
    setBusy(true);
    setFailed(false);
    const res = await commitDecision(id, {
      optionId: choice === DEFER ? null : choice,
      rationale,
      nextAction,
      reviewAt: review,
    });
    setBusy(false);
    if (res.error) return setFailed(true);
    setDone(true);
    setTimeout(() => navigate(`/keputusan/${id}`, { replace: true }), 1400);
  };

  if (state === "loading") return <div aria-busy="true" data-testid="commit-skeleton" className={styles.skeleton}><div /></div>;
  if (state === "missing") return <p role="alert" data-testid="commit-missing" className={styles.missing}>{t("ws.notFound")}</p>;

  const options = [
    ...d.options.map((o) => ({ value: o.id, label: o.name })),
    { value: DEFER, key: "defer", label: t("commit.defer") },
  ];

  return (
    <div data-testid="commit-page" className={styles.page}>
      <header className={styles.masthead}>
        <Link to={`/keputusan/${id}`} className={styles.back} data-testid="commit-back-link">
          <ArrowLeft size={16} strokeWidth={1.5} aria-hidden="true" />
          {t("commit.back")}
        </Link>
        <p className={styles.eyebrow}>{t("layer.decision")}</p>
        <h1 className={styles.title}>{t("commit.title")}</h1>
        <p className={styles.context} data-testid="commit-decision-title">{d.title}</p>
      </header>

      {recOption && (
        <div className={styles.recommendation}>
          <AiBlock title={t("commit.recLabel")} testId="commit-recommendation">
            <p className="m-0 text-base leading-6 text-ax-text [overflow-wrap:anywhere]">{recOption.name}</p>
            <p className="m-0 mt-1 font-mono text-[13px] leading-[18px] text-ax-text-2">
              {t("brief.confidence")} {rec.confidence.toFixed(2)}
            </p>
          </AiBlock>
        </div>
      )}

      <section className={styles.formPanel} aria-labelledby="commit-form-title" data-testid="commit-form-panel">
        <div className={styles.formHeader}>
          <div>
            <p className={styles.formLabel}>{t("layer.decision")}</p>
            <h2 id="commit-form-title" className={styles.formTitle}>{t("workspace.recordTitle")}</h2>
          </div>
          <Gavel size={20} strokeWidth={1.5} aria-hidden="true" />
        </div>
        <div className={styles.fields}>
          <details className={styles.choiceAccordion} data-testid="commit-choice-accordion">
            <summary className={styles.choiceSummary}>
              <span><span className="sr-only">{t("opt.list")}: </span><span className={styles.choiceValue} data-testid="commit-choice-summary">{options.find((o) => o.value === choice)?.label || t("commit.choosePlaceholder")}</span></span>
              <ChevronDown size={18} aria-hidden="true" />
            </summary>
            <div className={styles.choiceContent}>
          <RadioList
            legend={t("opt.list")}
            name="commit-choice"
            testId="commit-choice"
            value={choice}
            onChange={setChoice}
            options={options}
          />
            </div>
          </details>
          {differs && (
            <p data-testid="commit-differ-note" className="m-0 -mt-2 text-sm leading-5 text-ax-text-2">
              {t("commit.differ")}
            </p>
          )}
          <div>
            <Field
              multiline
              tone="dark"
              label={t("commit.rationale")}
              placeholder={t("commit.placeholder")}
              maxLength={800}
              value={rationale}
              onChange={(e) => setRationale(e.target.value)}
              onBlur={() => setTouched(true)}
              error={touched && rationale && short ? t("err.rationale") : undefined}
              hint={t("commit.hint")}
              testId="commit-rationale-input"
            />
            <p className="m-0 mt-1 text-right font-mono text-[13px] leading-[18px] text-ax-muted" data-testid="commit-rationale-count">
              {rationale.length} / 800
            </p>
          </div>
          <div className={styles.followUp}>
            <Field
              tone="dark"
              label={t("commit.nextAction")}
              maxLength={160}
              value={nextAction}
              onChange={(e) => setNextAction(e.target.value)}
              testId="commit-next-action-input"
            />
            <Field
              tone="dark"
              label={t("commit.reviewDate")}
              hint={t("commit.reviewHint")}
              type="date"
              min={isoDay(new Date())}
              value={review}
              onChange={(e) => setReview(e.target.value)}
              testId="commit-review-input"
            />
          </div>
        </div>
      </section>

      <div className={styles.feedback}>
        <CommitSeal play={done} />
        <p role="status" data-testid="commit-status" className="m-0 mt-2 min-h-[20px] text-sm leading-5 text-ax-text-2">
          {done ? t("commit.ok") : ""}
        </p>
        {failed && (
          <p role="alert" data-testid="commit-error" className="m-0 text-sm leading-5 text-ax-error">
            {t("commit.fail")}
          </p>
        )}
      </div>

      <PrimaryBar>
        <Button
          variant="primary"
          busy={busy}
          disabled={!valid || busy || done}
          onClick={submit}
          data-testid="commit-submit-button"
          className="w-full md:w-auto md:min-w-[200px]"
        >
          <LockKeyhole size={18} strokeWidth={1.5} aria-hidden="true" />
          {busy ? t("commit.busy") : t("commit.submit")}
        </Button>
      </PrimaryBar>

      <Sheet open={blocker.state === "blocked"} onOpenChange={(o) => !o && blocker.reset?.()} title={t("dirty.title")} testId="dirty-sheet">
        <Button variant="primary" onClick={() => blocker.reset()} data-testid="dirty-stay-button" className="w-full">
          {t("dirty.stay")}
        </Button>
        <Button onClick={() => blocker.proceed()} data-testid="dirty-discard-button" className="mt-2 w-full">
          {t("dirty.discard")}
        </Button>
      </Sheet>
    </div>
  );
}
